import React, {useCallback, useEffect, KeyboardEvent as ReactKeyboardEvent} from 'react';
import {observer} from 'mobx-react';
import {defaultRowsNum, headerHeight, rowHeight} from '../consts/grid';
import {Table} from './Table';
import {formsStore} from '../../../stores/Form/FormsStore';
import {Column} from '../../../stores/Form/Column';
import {Form} from '../../../stores/Form/Form';
import {isRTL} from '../../../Localization';
import {HeaderCellContent} from './HeaderCellContent';
import {OpenTableMethods} from './Table/types';
import {activateCurEntityBySession} from '../../../_shared/GWTBridge/invoke/genPage';
import {CellContent} from './CellContent';
import {cx_menu_input_anchor} from './FormInput/constants';
import {Splitter} from './Spitter';
import {SuspendedJobTypes} from '../consts/suspendedJobTypes';
import {
    prefetchSubFormDataOnRowChange,
    updateCustomColumnWidth,
    updateTableHeight
} from '../actions/form';
import {isNavigationLocked} from '../utils/isNavigationLocked';
import styles from '../styles/grid.module.scss';
import {keys} from '../../../constants/KeyCodes';
import debounce from 'lodash.debounce';

type Props = {
    currentForm: Form;
    isSubForm?: boolean;
    isApiLock: () => boolean;
    openSelectListModal: (lisType) => void;
    isActiveForm: boolean;
    defaultHeight?: number;
};

const emptyRecords = [];
const rtl = isRTL();

export const Grid = observer(
    ({currentForm, openSelectListModal, isActiveForm, defaultHeight, isApiLock}: Props) => {
        const {currentScreen} = formsStore;
        const searchMode = currentForm?.searchModeLevel;
        const tableAPI = currentForm?.tableAPI;
        const records = !searchMode ? currentForm.records : emptyRecords;

        useEffect(() => {
            const macro = formsStore.recordingMacro;
            if (macro?.eventArray[macro?.eventArray.length - 1]?.type !== SuspendedJobTypes.Zoom) {
                formsStore.setEventForRecordingMacro({
                    type: SuspendedJobTypes.StartEntity,
                    formName: currentForm.ename
                });
            }
        }, [currentForm.ename]);

        const hasActiveSubForm = useCallback(() => {
            return currentForm?.level < formsStore.getActiveForm()?.level;
        }, [currentForm]);

        const debouncedPrefetchSubFormData = useCallback(
            debounce(async (form: Form, rowIndex: number) => {
                await prefetchSubFormDataOnRowChange(form, rowIndex);
            }, (formsStore.sysConstants?.activedelay?.Int as number) * 1000),
            [formsStore.sysConstants?.activedelay?.Int]
        );

        const onFocusCell = useCallback(
            async (currentCellIndex: number, rowIndex?: number) => {
                if (currentForm && formsStore.onBackground) {
                    activateCurEntityBySession(currentForm.session);
                }
                if (currentForm?.currentColumn) {
                    if (
                        currentForm.currentColumn.index !== currentCellIndex &&
                        currentForm.currentColumn.maxWidthMode
                    ) {
                        currentForm.currentColumn.setMaxWidthMode(false);
                        tableAPI?.recalcColumnStyles();
                    }
                    const oldRowIndex = currentForm.currentRow;
                    currentForm.setCurrentColumnIdByIndex(currentCellIndex);
                    currentForm.setCurrentRow(rowIndex);

                    const macro = formsStore.recordingMacro;
                    if (
                        macro?.eventArray[macro?.eventArray.length - 1]?.type ===
                        SuspendedJobTypes.Click
                    ) {
                        formsStore.setEventForRecordingMacro({
                            type: SuspendedJobTypes.FormGoto,
                            rowIdx: rowIndex,
                            colIdx: currentCellIndex,
                            formName: currentForm.ename
                        });
                    }
                    const activeSubForm = currentForm.activeSubForm;

                    if (activeSubForm && currentForm.currentRow !== oldRowIndex) {
                        activeSubForm.resetRecords();
                        if (activeSubForm.form.Ishtml) {
                            activeSubForm.setHTMLContent('');
                        }
                        if (formsStore.sysConstants?.activedelay?.Int) {
                            debouncedPrefetchSubFormData(currentForm, currentForm.currentRow);
                        }
                    }
                }
            },
            [currentForm, tableAPI, debouncedPrefetchSubFormData]
        );

        const onReady = useCallback(
            (openMethods: OpenTableMethods) => {
                currentForm.setTableAPI(openMethods);
            },
            [currentForm]
        );

        const onDestroy = useCallback(() => currentForm.setTableAPI(null), [currentForm]);

        const getRowsCount = useCallback(() => {
            return searchMode ? defaultRowsNum : records.length;
        }, [searchMode, records]);

        const checkRecordStatusAndSave = useCallback(async () => {
            const {onFieldFocusChange, handleFormLevel} = formsStore.currentScreen;

            if (isActiveForm) {
                const newCellIndex = currentForm.currentColumn?.index || 0;
                const newRowIndex = currentForm.currentRow + 1;

                await onFieldFocusChange(
                    {newCellIndex, newRowIndex, level: currentForm.level},
                    () => {
                        if (!currentForm.records[currentForm.currentRow]?.isTemporary) {
                            if (currentForm.records[newRowIndex]) {
                                tableAPI?.focusCell(newCellIndex, newRowIndex);
                            }
                        }
                    }
                );
            } else {
                await handleFormLevel(currentForm.level);
            }
        }, [currentForm, isActiveForm, tableAPI]);

        const beforeFocusCellChange = useCallback(
            async (
                cellIndex: number,
                rowIndex: number,
                action: (cellIndex: number, rowIndex: number) => void
            ) => {
                const payload = {
                    newCellIndex: cellIndex,
                    newRowIndex: rowIndex,
                    level: currentForm.level
                };
                await currentScreen.onFieldFocusChange(payload, action);
            },
            [currentScreen, currentForm.level]
        );

        const cellRenderer = useCallback(
            params => {
                const {rowData, column, rowIndex, isActiveCell} = params;
                const searchValue = currentForm.searchModeData[rowIndex]?.[column.id];
                const currField = rowData?.getField(column.id);
                return (
                    <CellContent
                        onBlur={debouncedPrefetchSubFormData?.cancel}
                        isActiveCell={isActiveCell}
                        rowIndex={rowIndex}
                        column={column}
                        searchMode={searchMode}
                        onMaxWidthMode={tableAPI?.recalcColumnStyles}
                        onInputFocus={currentForm.setFormActiveElement}
                        updateSearchValue={currentForm.updateSearchModeCellValue}
                        currField={currField}
                        searchValue={searchValue}
                        openSelectListModal={openSelectListModal}
                    />
                );
            },
            [searchMode, tableAPI, currentForm, openSelectListModal, debouncedPrefetchSubFormData]
        );

        const columns = currentForm.visibleColumns;
        const tableId = currentForm.session;

        const onColumnsPin = useCallback(
            async (count: number) => {
                await currentForm?.updateFixedColumnsCount(count);
            },
            [currentForm]
        );

        const headerRenderer = useCallback(
            (column: Column, pinned) => {
                const isSortedColumn = column.parentForm.sort.find(
                    item => Number(item.ColumnID) === column.id
                );
                return (
                    <HeaderCellContent
                        isActiveForm={isActiveForm}
                        column={column}
                        pinned={pinned}
                        onColumnsPin={onColumnsPin}
                        tableId={tableId}
                        isSorted={!!isSortedColumn}
                    />
                );
            },
            [onColumnsPin, tableId, isActiveForm]
        );

        const onCellResizeEnd = useCallback(
            async (newWidth: number, columnId: number) => {
                const activeColumn = currentForm.getColumnByID(columnId);

                activeColumn.setWidth(newWidth);
                activeColumn.setMaxWidthMode(false);

                tableAPI.recalcColumnStyles();
                await updateCustomColumnWidth(currentForm, columnId, newWidth);
            },
            [tableAPI, currentForm]
        );

        const handleHeightResize = useCallback(
            async (height: number) => {
                await updateTableHeight(currentForm, height);
            },
            [currentForm]
        );

        const onScroll = useCallback((left: number, top: number) => {
            currentForm.tableScrollOffset.top = top;
            currentForm.tableScrollOffset.left = left;
        }, []);

        const isNavLocked = isNavigationLocked(currentForm) || isApiLock?.();

        const onTableKeydown = useCallback(
            async (e: ReactKeyboardEvent<HTMLDivElement>) => {
                const {records} = currentForm;

                if (!isNavLocked && records.length === 1 && !searchMode) {
                    if (e.key === keys.ARROW_DOWN || e.key === keys.ARROW_UP) {
                        e.preventDefault();
                        e.stopPropagation();
                        await checkRecordStatusAndSave();
                        return;
                    }
                }
            },
            [currentForm, checkRecordStatusAndSave, searchMode]
        );

        return (
            <Splitter resizable onResizeEnd={handleHeightResize} defaultHeight={defaultHeight}>
                <Table
                    key={currentForm.key}
                    rounded={currentForm.level === 0}
                    blockedNavigation={isNavLocked}
                    isActive={isActiveForm}
                    id={tableId}
                    isRtl={rtl}
                    highlightCurrentRow={hasActiveSubForm()}
                    fixedColumnsNumber={currentForm?.fixedColumnsNumber}
                    componentClasses={styles.grid}
                    data={records}
                    rowsCount={getRowsCount()}
                    headerHeight={headerHeight}
                    rowHeight={rowHeight}
                    searchModeLevel={searchMode}
                    columns={columns}
                    headerRenderer={headerRenderer}
                    cellRenderer={cellRenderer}
                    cellClasses={cx_menu_input_anchor}
                    onFocusCell={onFocusCell}
                    onReady={onReady}
                    onDestroy={onDestroy}
                    activeCellIndex={currentForm?.currentColumn.index}
                    activeRowIndex={currentForm?.currentRow}
                    validateFocusChange={beforeFocusCellChange}
                    onTableBodyClick={checkRecordStatusAndSave}
                    onCellResizeEnd={onCellResizeEnd}
                    onScroll={onScroll}
                    onKeyDown={onTableKeydown}
                    defaultScrollOffsetLeft={currentForm.tableScrollOffset.left}
                    defaultScrollOffsetTop={currentForm.tableScrollOffset.top}
                />
            </Splitter>
        );
    }
);
